import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SavedSignatureCard } from "@/components/saved-signature-card";
import { isPastDateKey } from "@/lib/datetime";
import { ArrowLeft, Award, Gauge as GaugeIcon, GraduationCap } from "lucide-react";

interface CeCourse {
    id: string;
    courseName: string;
    provider: string | null;
    completedOn: string | null;
    hours: number | null;
}
interface MeProfile {
    identity: {
        firstName: string;
        lastName: string;
        email: string | null;
        roleName: string;
        tceqLicenseNumber: string | null;
        tceqLicenseExpiry: string | null;
    };
    licenses: Array<{
        id: string;
        label: string;
        jurisdiction: string;
        credentialId: string | null;
        expiresOn: string | null;
        isActive: boolean;
        ceCourses: CeCourse[];
    }>;
    gauges: Array<{
        id: string;
        serialNumber: string;
        make: string | null;
        model: string | null;
        calibrationDue: string | null;
        classification: string;
    }>;
}

function isExpired(dateStr: string | null): boolean {
    if (!dateStr) return false;
    return isPastDateKey(dateStr);
}

function ExpiryBadge({ date }: { date: string | null }) {
    if (!date) return <span className="text-muted-foreground">—</span>;
    return (
        <Badge variant={isExpired(date) ? "destructive" : "outline"} className="font-mono text-xs">
            {date}
        </Badge>
    );
}

export default function FieldProfilePage() {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["me-profile"],
        queryFn: () => api.get<{ profile: MeProfile }>("/api/me/profile"),
    });

    if (isLoading) {
        return <div className="p-6 text-foreground/70">Loading your credentials…</div>;
    }
    if (isError || !data?.profile) {
        return <div className="p-6 text-destructive">Could not load your credentials.</div>;
    }

    const p = data.profile;
    const fullName = `${p.identity.firstName} ${p.identity.lastName}`.trim();

    return (
        <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
            <Link
                to="/field"
                className="inline-flex items-center gap-1 text-sm text-shell-foreground/70 hover:text-shell-foreground"
            >
                <ArrowLeft className="h-4 w-4" /> Back
            </Link>

            <div>
                <h1 className="text-xl font-black tracking-tight text-shell-foreground">My credentials</h1>
                <p className="text-sm text-shell-foreground/70">
                    {fullName} · <span className="capitalize">{p.identity.roleName}</span>
                </p>
            </div>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Award className="h-4 w-4" /> Licenses
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {p.licenses.length === 0 ? (
                        <p className="text-sm text-foreground/60">No licenses on file.</p>
                    ) : (
                        p.licenses.map((l) => (
                            <div key={l.id} className="rounded-lg border p-3">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium">
                                        {l.label} <span className="text-foreground/60">({l.jurisdiction})</span>
                                    </span>
                                    <ExpiryBadge date={l.expiresOn} />
                                </div>
                                {l.credentialId && (
                                    <div className="mt-0.5 text-sm text-foreground/70">#{l.credentialId}</div>
                                )}
                                {l.ceCourses.length > 0 && (
                                    <div className="mt-2 flex items-center gap-1 text-xs text-foreground/60">
                                        <GraduationCap className="h-3.5 w-3.5" />
                                        {l.ceCourses.length} continuing-education (CE) course
                                        {l.ceCourses.length === 1 ? "" : "s"} on file
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <GaugeIcon className="h-4 w-4" /> My gauges
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {p.gauges.length === 0 ? (
                        <p className="text-sm text-foreground/60">No gauges assigned.</p>
                    ) : (
                        <ul className="divide-y">
                            {p.gauges.map((g) => (
                                <li key={g.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                                    <span className="font-medium">
                                        {g.serialNumber}
                                        <span className="text-foreground/60">
                                            {" "}
                                            {[g.make, g.model].filter(Boolean).join(" ")}
                                        </span>
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <Badge variant="secondary" className="capitalize">
                                            {g.classification.replace("_", " ")}
                                        </Badge>
                                        <ExpiryBadge date={g.calibrationDue} />
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <SavedSignatureCard />
        </div>
    );
}
